import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { updatePlayerProfile } from "@/services/player/update-player-profile";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { CountryPicker } from "@/components/ui/country-picker/country-picker";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { RouteNames } from "@/types/route-names";
import { BackIcon } from "@/icons";

export const ProfileSettingsView = () => {
  const { state: authState, getAccessToken } = useAuth();
  const { setCurrentRoute } = useSPAContext();

  const [country, setCountry] = useState<string | undefined>(undefined);
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const res = await getPlayerProfile(token);
        if (res.ok && res.data?.country) {
          setCountry(res.data.country);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchProfile();
  }, [getAccessToken]);

  const handleCountrySelect = async (countryCode: string) => {
    setCountry(countryCode);
    setIsCountryPickerOpen(false);

    const token = getAccessToken();
    if (token) {
      await updatePlayerProfile(token, { country: countryCode });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 w-full max-w-md mx-auto">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setCurrentRoute(RouteNames.PLAYER_PROFILE)}
          className="text-pk-gray-5 hover:text-pk-blue-4 transition-colors"
        >
          <BackIcon />
        </button>
        <h1 className="text-2xl font-bold text-pk-gray-5">Profile Settings</h1>
      </div>

      <StandardFrame
        variant="gray"
        className="p-4 flex flex-col gap-4 bg-pk-gray-1"
      >
        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-pk-gray-4 uppercase">
            Username
          </label>
          <div className="p-3 bg-pk-gray-2 border-2 border-pk-gray-3 rounded-lg text-pk-gray-5 font-bold">
            {authState.user?.username}
          </div>
          <p className="text-xs text-pk-gray-4">Username cannot be changed.</p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-bold text-pk-gray-4 uppercase">
            Country
          </label>
          <button
            onClick={() => setIsCountryPickerOpen(true)}
            className="flex items-center justify-between p-3 bg-white border-2 border-pk-gray-3 rounded-lg hover:border-pk-blue-4 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <CountryFlag countryCode={country} size="md" />
              <span className="text-pk-gray-5 font-bold">
                {country || "Select Country"}
              </span>
            </div>
            <span className="text-pk-gray-3 group-hover:text-pk-blue-4 text-sm font-bold uppercase">
              Edit
            </span>
          </button>
        </div>
      </StandardFrame>

      {isCountryPickerOpen && (
        <CountryPicker
          selectedCountry={country}
          onSelect={handleCountrySelect}
          onClose={() => setIsCountryPickerOpen(false)}
        />
      )}
    </div>
  );
};
